'use client'
import React, { useEffect, useState } from 'react'
import parse from 'html-react-parser'
import { Box, Grid, GridProps, SxProps, Typography, List, ListItem } from '@mui/material'
import { useTranslations } from 'next-intl'
import { HEADER_HEIGHT_PX } from '@/constants'

type Props = {
  content: any
  sx?: SxProps
  gridProps?: Omit<GridProps, 'children'>
}

export const BlogCategories = ({ content, sx, gridProps }: Props) => {
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [isOpenToc, setIsOpenToc] = useState<boolean>(false)
  const t = useTranslations()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentId(window.location.hash)
    }
  }, [])

  const handleScroll = (id: string) => {
    const targetElement = document.getElementById(id)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.history.pushState(null, '', `#${id}`)
    }
  }
  const handleScrollEvent = () => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6'))
    let closestToCenterElement: any = null
    let closestDistanceToCenter = Infinity
    const viewportCenter = window.innerHeight / 2 // Tính trung tâm màn hình

    // Lọc các phần tử nằm trong viewport
    const inViewportElements = elements.filter((element) => {
      const rect = element.getBoundingClientRect()
      return rect.top >= 0 && rect.bottom <= window.innerHeight
    })

    // Nếu có các phần tử trong viewport, tìm phần tử gần trung tâm màn hình nhất
    inViewportElements.forEach((element) => {
      const rect = element.getBoundingClientRect()
      const elementCenter = rect.top + rect.height / 2 // Trung tâm của phần tử
      const distanceToCenter = Math.abs(viewportCenter - elementCenter) // Khoảng cách đến trung tâm màn hình

      if (distanceToCenter < closestDistanceToCenter) {
        closestToCenterElement = element
        closestDistanceToCenter = distanceToCenter
      }
    })

    // Nếu tìm thấy phần tử gần trung tâm nhất trong viewport, cập nhật currentId
    if (closestToCenterElement) {
      setCurrentId(closestToCenterElement.id)
      setIsOpenToc(false) // Đóng menu nếu đang mở
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScrollEvent)
    return () => {
      window.removeEventListener('scroll', handleScrollEvent)
    }
  }, [])

  const extractText = (children: any): string => {
    if (!children) {
      return ''
    }
    if (typeof children === 'string' || typeof children === 'number') {
      return children.toString()
    }
    if (Array.isArray(children)) {
      return children.map(extractText).join('')
    }
    if (React.isValidElement(children as any)) {
      return extractText(children.props.children)
    }
    return ''
  }
  const extractTitlesAndIds = (node: any) => {
    const result: any = {
      titles: [],
      h1Title: null,
    }

    const traverseNode = (node: any) => {
      if (node && node.props && node.props.id) {
        const tagName = node.type

        if (tagName === 'h1' && !result.h1Title) {
          result.h1Title = extractText(node.props.children)
        }

        if (['h1', 'h2', 'h3', 'h4', 'h5', 'span'].includes(tagName)) {
          const item = {
            title: extractText(node.props.children),
            id: node.props.id,
            children: [],
          }

          if (tagName === 'h2') {
            result.titles.push(item)
          } else if (tagName === 'h3') {
            const lastH2 = result.titles[result.titles.length - 1]
            if (lastH2) {
              lastH2.children.push(item)
            } else {
              result.titles.push(item) // Fallback trong trường hợp không có h2
            }
          } else if (tagName === 'h4') {
            const lastH2 = result.titles[result.titles.length - 1]
            const lastH3 = lastH2?.children[lastH2.children.length - 1]
            if (lastH3) {
              lastH3.children.push(item)
            } else if (lastH2) {
              lastH2.children.push(item) // Fallback nếu không có h3
            } else {
              result.titles.push(item) // Fallback nếu không có h2
            }
          } else if (tagName === 'h5') {
            const lastH2 = result.titles[result.titles.length - 1]
            const lastH3 = lastH2?.children[lastH2.children.length - 1]
            const lastH4 = lastH3?.children[lastH3.children.length - 1]
            if (lastH4) {
              lastH4.children.push(item)
            } else if (lastH3) {
              lastH3.children.push(item) // Fallback nếu không có h4
            } else if (lastH2) {
              lastH2.children.push(item) // Fallback nếu không có h3
            } else {
              result.titles.push(item) // Fallback nếu không có h2
            }
          }
        }
      }

      if (node && node.props && node.props.children) {
        React.Children.forEach(node.props.children, (child) => {
          traverseNode(child)
        })
      }
    }

    traverseNode(node)

    return result
  }
  const parsedHtml = parse(content || '')

  // Đảm bảo parsedHtml là mảng để có thể sử dụng flatMap
  const parsedArray = Array.isArray(parsedHtml) ? parsedHtml : [parsedHtml]

  const titlesAndIds: any = parsedArray?.flatMap((node: any) => extractTitlesAndIds(node))
  const menuChild = (item: any, childIndexContent: any) => {
    if (item?.children?.length === 0) {
      return null
    }
    return (
      <List
        sx={{
          marginLeft: '0.5em',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: 0,
        }}
      >
        {item?.children?.map((child: any, index: number) => {
          let childIndexContentInner = childIndexContent + '.' + (index + 1)
          return (
            <ListItem key={child?.id} sx={{ padding: 0 }}>
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    cursor: 'pointer',
                    color: currentId === child?.id ? 'primary.main' : '',
                  }}
                  onClick={() => handleScroll(child?.id)}
                >
                  {childIndexContentInner}. {parse(child?.title) || ''}
                </Box>
                {menuChild(child, childIndexContentInner)}
              </Box>
            </ListItem>
          )
        })}
      </List>
    )
  }
  if (titlesAndIds?.[0]?.titles?.length <= 0) {
    return null
  }
  return (
    <Grid {...gridProps}>
      <Box
        sx={{
          color: isOpenToc ? 'primary.main' : '',
          background: (theme) => (theme.palette.mode === 'dark' ? '#000' : '#F4F5F8'),
          borderRadius: { xs: '4px', md: '8px' },
          position: { xs: 'static', sm: 'sticky' },
          top: { xs: HEADER_HEIGHT_PX.TOTAL.xs, md: '32px' },
          maxHeight: { xs: 'calc(100vh - var(--sz-hd) + 1rem)', md: 'calc(100vh - 32px - 1rem)' },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          ...sx,
        }}
      >
        <Typography
          sx={{ marginBottom: titlesAndIds?.[0]?.titles?.length > 0 ? '8px' : '', fontWeight: 600 }}
        >
          {t('Product.categories')}
        </Typography>
        <List sx={{ display: 'flex', flexDirection: 'column', gap: '0.5em', padding: 0 }}>
          {titlesAndIds?.[0]?.titles?.length > 0 && (
            <List component="div" sx={{ padding: 0 }}>
              {titlesAndIds?.[0]?.titles?.map((item: any, index: number) => (
                <ListItem key={item?.id} sx={{ padding: 0, display: 'block' }}>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      color: currentId === item?.id ? 'primary.main' : '',
                      marginBottom: '8px',
                      fontWeight: 600,
                    }}
                    onClick={() => handleScroll(item?.id)}
                  >
                    {parse(item?.title) || ''}
                  </Box>
                  {item?.children?.length > 0 && (
                    <List
                      sx={{
                        marginLeft: '0.5em',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        padding: 0,
                      }}
                    >
                      {item?.children?.map((child: any, childIndex: number) => {
                        const childIndexContent = index + 1 + '.' + (childIndex + 1)
                        return (
                          <ListItem key={child?.id} sx={{ padding: 0 }}>
                            <Box sx={{ width: '100%' }}>
                              <Box
                                sx={{
                                  cursor: 'pointer',
                                  color: currentId === child?.id ? 'primary.main' : '',
                                }}
                                onClick={() => handleScroll(child?.id)}
                              >
                                {childIndexContent}. {parse(child?.title) || ''}
                              </Box>
                              {menuChild(child, childIndexContent)}
                            </Box>
                          </ListItem>
                        )
                      })}
                    </List>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </List>
      </Box>
      {/* {titlesAndIds?.[0]?.titles?.length > 1 && (
        <Box
          sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.5rem',
            height: '2.5rem',
            position: 'fixed',
            right: '10px',
            top: '50%',
            zIndex: 50,
            transform: 'translateY(-50%)',
            borderRadius: '0.5em',
            backgroundColor: 'primary.main',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={() => setIsOpenToc(!isOpenToc)}
        >


        </Box>
      )} */}
    </Grid>
  )
}
